/**
 * DigestPreviewPanel — Iter-165 · Phase 4.
 *
 * Finance-only panel inside the Billing & A/R tab that lets an
 * operator select any PM, preview their weekly digest inline, and
 * optionally trigger an immediate send. Preview is read-only — no
 * notifications are marked as read.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Mail, Send, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DigestPreviewPanel() {
  const [pms, setPms] = useState([]);
  const [selected, setSelected] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const loadPms = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/billing-notifications/pm-digest/pm-list`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setPms(d.pms || []); }
    } catch (_) {}
  }, []);
  useEffect(() => { loadPms(); }, [loadPms]);

  const runPreview = async () => {
    if (!selected) return;
    setBusy(true); setMsg(''); setPreview(null);
    try {
      const r = await fetch(`${API}/api/billing-notifications/pm-digest/preview?user_id=${encodeURIComponent(selected)}`,
                              { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setPreview(d);
      else setMsg(d.detail || 'Preview failed.');
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); }
    setBusy(false);
  };

  const sendNow = async () => {
    if (!selected) return;
    setSending(true); setMsg('');
    try {
      const r = await fetch(`${API}/api/billing-notifications/pm-digest/preview?user_id=${encodeURIComponent(selected)}&send=true`,
                              { credentials: 'include' });
      const d = await r.json();
      if (r.ok && d.sent) setMsg(`Digest emailed to ${d.email}.`);
      else if (r.ok) setMsg(`Preview generated but send failed (Resend rejected or key missing).`);
      else setMsg(d.detail || 'Send failed.');
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); }
    setSending(false);
  };

  return (
    <Card className="p-4 bg-[#141414] border-zinc-800 space-y-3" data-testid="digest-preview-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-400" />
          <p className="text-[10px] uppercase tracking-widest text-zinc-300">Preview Weekly PM Digest</p>
        </div>
        <Button size="sm" onClick={loadPms}
                className="text-[10px] uppercase"
                style={{ backgroundColor: '#27272a', color: '#fff' }}
                data-testid="digest-preview-refresh-list">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh PMs
        </Button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
                className="rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2 flex-1 min-w-[220px]"
                data-testid="digest-preview-pm-select">
          <option value="">Select a Property Manager…</option>
          {pms.map((p) => {
            const optLabel = `${p.full_name || p.email || p.user_id} — ${p.property_id || 'no property'}`;
            return (
              <option key={p.user_id} value={p.user_id} label={optLabel}>{optLabel}</option>
            );
          })}
        </select>
        <Button size="sm" onClick={runPreview} disabled={!selected || busy}
                className="text-[10px] uppercase font-bold"
                style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                data-testid="digest-preview-run">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Mail className="h-3 w-3 mr-1" />}
          Preview digest
        </Button>
        {preview && (
          <Button size="sm" onClick={sendNow} disabled={sending}
                  className="text-[10px] uppercase font-bold"
                  style={{ backgroundColor: '#10b981', color: '#fff' }}
                  data-testid="digest-preview-send">
            {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
            Send now
          </Button>
        )}
      </div>
      {msg && <p className="text-xs" style={{ color: '#7CFC8A' }} data-testid="digest-preview-msg">{msg}</p>}
      {preview && (
        <div className="rounded border border-zinc-800 bg-white overflow-auto max-h-[520px] p-1"
             data-testid="digest-preview-body">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-3 pt-2">
            {preview.email} · {preview.notifications_count} event(s) · {preview.call_tasks_count} call task(s)
          </div>
          <iframe title="digest-preview" srcDoc={preview.html}
                  className="w-full h-[480px] border-0"
                  data-testid="digest-preview-iframe" />
        </div>
      )}
    </Card>
  );
}
